import type { ThreadFact, ThreadQuality, ThreadTone } from "@/lib/conversation/thread-header"
import { capitalize } from "@/lib/utils"

import type {
  Cohort,
  EscalationCategory,
  HumanOutcome,
  SessionOutcome,
  TakeoverReason,
  TopicCategory,
} from "./outcomes"

/**
 * How the taxonomy is shown. Separate from `outcomes.ts` so the module the SQL CHECK
 * constraints are mirrored against carries no UI vocabulary — and so a label can change
 * without touching the values Postgres enforces.
 *
 * Client-safe: the badge, the correction dialog and the analytics all read it.
 */

export const OUTCOME_LABELS: Record<SessionOutcome, string> = {
  resolved_positive: "Resolved",
  resolved_neutral: "Assumed resolved",
  // Not «Escalated»: in the inbox that word already marks the stamped fact on a reply, and the
  // service metrics use it for the agent asking for a person. This verdict is broader — it also
  // covers a colleague stepping in unasked — so it must not read as the same number.
  escalated: "Handed to team",
  unresolved_left: "Left unresolved",
  abandoned: "Abandoned",
  spam: "Not a request",
}

/**
 * What each verdict means, in a line — the legends carry it, so a colour never stands unexplained. A paraphrase of
 * the `outcome` rubric in `review/schema.ts`, written for the operator: re-read it whenever that rubric changes.
 */
export const OUTCOME_DEFINITIONS: Record<SessionOutcome, string> = {
  resolved_positive: "The customer got what they came for and said so, or acted on it.",
  resolved_neutral: "The conversation stopped after an answer that closes the question — no thanks, no complaint.",
  escalated:
    "It ended in people's hands: the agent handed it over, a colleague stepped in, or the customer was sent to your team by email or phone.",
  unresolved_left:
    "The customer left dissatisfied with the agent's replies — refusing what it asked, complaining, or repeating the question.",
  abandoned: "The customer never got a real answer; the last thing they saw was a question back.",
  spam: "Nobody was asking for anything — a story mention, a bot, a lone emoji or reaction.",
}

export const OUTCOME_TONES: Record<SessionOutcome, ThreadTone> = {
  resolved_positive: "success",
  resolved_neutral: "success",
  escalated: "info",
  unresolved_left: "warning",
  abandoned: "warning",
  spam: "neutral",
}

/**
 * One colour per outcome wherever outcomes are stacked into a bar, so a segment keeps its colour from chart to chart —
 * and the colour carries how the ending reads: the two resolutions green, one deeper than the other, a handover blue
 * because it is a path and not a failure, an unresolved ending amber, an abandoned one rose. Its own tokens, not
 * `--chart-N`, whose hues are shuffled between the light and the dark theme; each is picked per theme to clear 3:1
 * against the card and the bar's track. Never red against green alone either: the bar's order and its legend say the
 * same thing without colour.
 */
export const OUTCOME_CHART_COLORS: Record<SessionOutcome, string> = {
  resolved_positive: "hsl(var(--outcome-resolved))",
  resolved_neutral: "hsl(var(--outcome-assumed))",
  escalated: "hsl(var(--outcome-handover))",
  unresolved_left: "hsl(var(--outcome-unresolved))",
  abandoned: "hsl(var(--outcome-abandoned))",
  spam: "hsl(var(--muted-foreground))",
}

/** «Resolved» where it is a background rather than a segment — the same green, half as loud. */
export const FADED_RESOLVED = "hsl(var(--outcome-resolved) / 0.5)"

/** Who handled a session is not how it ended, so the split takes the page's own ink, not an outcome's colour. */
export const COHORT_CHART_COLORS: Record<Cohort, string> = {
  aiOnly: "hsl(var(--cohort-strong))",
  teamInvolved: "hsl(var(--cohort-mid))",
  unattended: "hsl(var(--cohort-faint))",
}

/** The same colours where the names match, so «Resolved» looks alike in both charts. */
export const HUMAN_OUTCOME_CHART_COLORS: Record<HumanOutcome, string> = {
  resolved_positive: OUTCOME_CHART_COLORS.resolved_positive,
  resolved_neutral: OUTCOME_CHART_COLORS.resolved_neutral,
  unresolved_left: OUTCOME_CHART_COLORS.unresolved_left,
  unhandled: OUTCOME_CHART_COLORS.abandoned,
}

export const ESCALATION_CATEGORY_LABELS: Record<EscalationCategory, string> = {
  knowledge_gap: "Knowledge gap",
  out_of_scope_action: "Outside the agent's reach",
  customer_preference: "Customer asked for a person",
  emotional: "Emotional situation",
  policy_boundary: "Policy boundary",
  other: "Other",
}

export const TAKEOVER_REASON_LABELS: Record<TakeoverReason, string> = {
  ai_answered_poorly: "AI answered poorly",
  customer_frustrated: "Customer was frustrated",
  sales_opportunity: "Sales opportunity",
  no_visible_reason: "No visible reason",
}

export const TOPIC_CATEGORY_LABELS: Record<TopicCategory, string> = {
  product_information: "Product information",
  product_fit: "Fit and choice",
  pricing_and_payment: "Pricing and payment",
  availability: "Availability",
  orders_and_delivery: "Orders and delivery",
  changes_and_returns: "Changes and returns",
  booking: "Booking",
  account_and_access: "Account and access",
  technical_issue: "Technical issue",
  business_inquiry: "Business inquiry",
  contact_request: "Contact request",
  other: "Other",
}

/** A question as it reads beside its kind; the kind is left out where the question is named as it. */
export function questionWithKind(label: string, category: TopicCategory): string {
  const question = capitalize(label)
  const kind = TOPIC_CATEGORY_LABELS[category]
  return question.toLowerCase() === kind.toLowerCase() ? question : `${question} (${kind})`
}

export const HUMAN_OUTCOME_LABELS: Record<HumanOutcome, string> = {
  resolved_positive: "Resolved",
  resolved_neutral: "Assumed resolved",
  unresolved_left: "Left unresolved",
  unhandled: "Nobody replied",
}

/** The team's part is judged on the session's own scale, so the three shared endings read the same. */
export const HUMAN_OUTCOME_DEFINITIONS: Record<HumanOutcome, string> = {
  resolved_positive: OUTCOME_DEFINITIONS.resolved_positive,
  resolved_neutral: OUTCOME_DEFINITIONS.resolved_neutral,
  unresolved_left: OUTCOME_DEFINITIONS.unresolved_left,
  unhandled: "Nobody from your team wrote in the conversation after the handover.",
}

/**
 * The session summary as the header renders it — one full-width row, like a call summary,
 * because no pill holds two sentences.
 *
 * Shared so both inboxes show it under the same label: the badge already appears in both, and
 * a summary present in one and absent in the other reads as a missing summary.
 */
export function sessionSummaryFacts(quality: ThreadQuality | null | undefined): ThreadFact[] {
  return quality?.summary ? [{ label: "Summary", value: quality.summary, block: true }] : []
}
